import type { TabTaskStatus } from './tab_task_badge_policy';
import executingUrl from '/@/assets/images/tab_task_badge/executing.svg';
import successUrl from '/@/assets/images/tab_task_badge/success.svg';
import failUrl from '/@/assets/images/tab_task_badge/fail.svg';
import interruptUrl from '/@/assets/images/tab_task_badge/interrupt.svg';

export const DEFAULT_FAVICON_HREF = '/favicon.ico';

let faviconWriteSeq = 0;

/** Tab 展示约 16px；角标按 6/16 叠在右下角，业务 icon 铺满画布不缩小。 */
export const FIGMA_ICON_PX = 16;
export const FIGMA_BADGE_PX = 7;

/** 64px source so Chrome's ~16px tab icon stays sharper than a 32px canvas. */
export const BADGE_CANVAS_SIZE = 64;

const BADGE_SRC: Record<Exclude<TabTaskStatus, 'idle'>, string> = {
  executing: executingUrl,
  success: successUrl,
  fail: failUrl,
  interrupt: interruptUrl,
};

export function getFaviconBadgeLayout(canvasSize = BADGE_CANVAS_SIZE): {
  iconSize: number;
  badgeSize: number;
  badgeOffset: number;
} {
  const badgeSize = Math.round((canvasSize * FIGMA_BADGE_PX) / FIGMA_ICON_PX);
  return {
    iconSize: canvasSize,
    badgeSize,
    badgeOffset: canvasSize - badgeSize,
  };
}

export function setFaviconHref(href: string): void {
  document.querySelectorAll<HTMLLinkElement>("link[rel~='icon']").forEach((el) => el.remove());
  const link = document.createElement('link');
  link.rel = 'icon';
  link.href = href.startsWith('data:')
    ? href
    : `${href}${href.includes('?') ? '&' : '?'}tb=${++faviconWriteSeq}`;
  document.head.appendChild(link);
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`failed to load image: ${src}`));
    image.src = src;
  });
}

export async function composeFaviconWithBadge(
  status: Exclude<TabTaskStatus, 'idle'>,
): Promise<string> {
  const canvas = document.createElement('canvas');
  canvas.width = BADGE_CANVAS_SIZE;
  canvas.height = BADGE_CANVAS_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('canvas 2d context unavailable');
  }
  ctx.imageSmoothingEnabled = true;
  if ('imageSmoothingQuality' in ctx) {
    ctx.imageSmoothingQuality = 'high';
  }

  const { iconSize, badgeSize, badgeOffset } = getFaviconBadgeLayout();
  const base = await loadImage(DEFAULT_FAVICON_HREF);
  ctx.drawImage(base, 0, 0, iconSize, iconSize);
  const badge = await loadImage(BADGE_SRC[status]);
  ctx.drawImage(badge, badgeOffset, badgeOffset, badgeSize, badgeSize);
  return canvas.toDataURL('image/png');
}
